import { ChangeDetectionStrategy, Component, DestroyRef, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { GenericModalComponent, ModalConfig } from '../generic-modal/generic-modal.component';
import { from, switchMap, tap, catchError, of, firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UsersService, UpdateUserPermissionsRequest, SendUserInviteRequest, SendUserInviteResponse } from '../../services/users.service';
import { UserSessionService } from '../../services/user-session.service';
import { DateFormatterService } from '../../services/date-formatter.service';
import { environment } from '../../../../environments/environment';

export interface UserDetailData {
  id?: string;
  name: string;
  email: string;
  displayName?: string;
  initials: string;
  avatarColor: string;
  joinedDate?: string;
  termsAccepted?: boolean;
  status?: number; // 1=Draft, 2=PendingFirstLogin, 3=Active, 4=SyncError, 5=Inactive
  authorizations?: {
    tasks: boolean;
    entities: boolean;
    documents: boolean;
    invoices: boolean;
    messages: boolean;
    shares: boolean;
    relatedParties: boolean;
    requests: boolean;
    banking: boolean;
    assets: boolean;
    investments: boolean;
    information: boolean;
    loans: boolean;
  };
}

@Component({
  selector: 'app-user-detail-modal',
  templateUrl: './user-detail-modal.component.html',
  styleUrls: ['./user-detail-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, AngularSvgIconModule, GenericModalComponent]
})
export class UserDetailModalComponent implements OnInit, OnChanges {
  @Input() isOpen: boolean = false;
  @Input() userData?: UserDetailData;
  
  @Output() closeModal = new EventEmitter<void>();
  @Output() userAction = new EventEmitter<{ action: string, user: UserDetailData }>();
  @Output() updateUserPermissions = new EventEmitter<UserDetailData>();
  @Output() deleteUser = new EventEmitter<string>(); // Emit user ID when deleted
  @Output() userStatusChanged = new EventEmitter<{ userId: string, status: number }>(); // Emit when user status changes
  @Output() termsReset = new EventEmitter<UserDetailData>(); // Emit updated user data when terms are reset
  @Output() userUpdated = new EventEmitter<UserDetailData>(); // Emit updated user data when display name is updated

  // Inject the users service
  private readonly usersService = inject(UsersService);
  private readonly userSessionService = inject(UserSessionService);
  private readonly dateFormatterService = inject(DateFormatterService);
  private readonly destroyRef = inject(DestroyRef);

  // Track original permissions to detect changes
  private originalAuthorizations?: UserDetailData['authorizations'];
  
  // Current editable permissions
  editableAuthorizations?: UserDetailData['authorizations'];
  
  // Track if permissions have changed and if update is in progress
  readonly hasPermissionsChanged = signal(false);
  readonly isUpdating = signal(false);
  readonly isDeleting = signal(false);
  readonly isSendingInvite = signal(false);
  readonly isResettingTerms = signal(false);
  readonly activeActionTab = signal<'manage' | 'invite'>('manage');
  readonly combinedActionMessage = signal('');
  readonly combinedActionError = signal('');


  modalConfig: ModalConfig = {
    title: 'User Details',
    width: '90vw',
    maxWidth: '600px',
    maxHeight: '90vh',
    showCloseButton: true,
    closeOnBackdropClick: true,
    showExpandButton: true,
    expandedWidth: '98vw',
    expandedMaxWidth: '1600px',
    expandedHeight: '90vh',
    expandedMaxHeight: '900px'
  };

  ngOnInit() {
    this.initializePermissions();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.initializePermissions();
    if (changes['isOpen'] && !this.isOpen) {
      this.isSendingInvite.set(false);
      this.isUpdating.set(false);
      this.isDeleting.set(false);
      this.isResettingTerms.set(false);
      this.combinedActionMessage.set('');
      this.combinedActionError.set('');
    }
  }

  private initializePermissions() {
    const defaultAuthorizations = {
      tasks: false,
      entities: false,
      documents: false,
      invoices: false,
      messages: false,
      shares: false,
      relatedParties: false,
      requests: false,
      banking: false,
      assets: false,
      investments: false,
      information: false,
      loans: false
    };

    if (this.userData?.authorizations) {
      // Create deep copies to track changes
      this.originalAuthorizations = { ...this.userData.authorizations };
      this.editableAuthorizations = { ...this.userData.authorizations };
    } else {
      // Initialize with default values if no authorizations provided
      this.originalAuthorizations = { ...defaultAuthorizations };
      this.editableAuthorizations = { ...defaultAuthorizations };
    }
    this.hasPermissionsChanged.set(false);
  }

  onPermissionChange() {
    this.checkForChanges();
  }

  private checkForChanges() {
    // Copy to locals so the type guard applies (class fields are not narrowed reliably)
    const original = this.originalAuthorizations;
    const editable = this.editableAuthorizations;

    if (!original || !editable) {
      this.hasPermissionsChanged.set(false);
      return;
    }

    // Compare each permission to detect changes
    const authKeys = Object.keys(original) as Array<keyof typeof original>;
    this.hasPermissionsChanged.set(authKeys.some(key => original[key] !== editable[key]));
  }

  // Default placeholder data
  getDefaultUserData(): UserDetailData {
    return {
      name: this.userData?.displayName || this.userData?.name || 'Unknown User',
      email: this.userData?.email || 'unknown@example.com',
      initials: this.userData?.initials || this.getInitialsFromName(this.userData?.displayName || this.userData?.name || 'Unknown User'),
      avatarColor: this.userData?.avatarColor || '#3b82f6',
      joinedDate: this.userData?.joinedDate || '-',
      termsAccepted: this.userData?.termsAccepted ?? true,
      authorizations: this.userData?.authorizations || {
        tasks: false,
        entities: false,
        documents: false,
        invoices: false,
        messages: false,
        shares: false,
        relatedParties: false,
        requests: false,
        banking: false,
        assets: false,
        investments: false,
        information: false,
        loans: false
      }
    };
  }

  onCloseModal() {
    this.closeModal.emit();
  }

  private getInitialsFromName(name: string): string {
    if (!name) return '??';
    const names = name.trim().split(' ');
    if (names.length === 1) {
      return names[0].substring(0, 2).toUpperCase();
    }
    return (names[0].charAt(0) + (names.at(-1)?.charAt(0) ?? '')).toUpperCase();
  }

  onUpdatePermissions() {
    if (this.hasPermissionsChanged() && this.editableAuthorizations && this.userData?.id) {
      this.isUpdating.set(true);
      
      const updateRequest: UpdateUserPermissionsRequest = {
        userId: this.userData.id,
        authorizations: { ...this.editableAuthorizations }
      };

      // Capture stable references for use inside async callback
      const currentUserData = this.userData;
      const editableAuth = this.editableAuthorizations;

      this.usersService.updateUserPermissions(updateRequest).subscribe({
        next: (_updatedUser) => {
          // Update the original authorizations to reflect the new state
          this.originalAuthorizations = { ...this.editableAuthorizations };
          this.hasPermissionsChanged.set(false);
          this.isUpdating.set(false);
          
          // Emit the updated user data
          if (!currentUserData || !editableAuth) {
            // State changed unexpectedly; nothing to emit
            return;
          }
          const updatedUserData: UserDetailData = {
            ...currentUserData,
            authorizations: { ...editableAuth }
          };
          this.updateUserPermissions.emit(updatedUserData);
        },
        error: (error) => {
          console.error('Failed to update user permissions:', error);
          this.isUpdating.set(false);
        }
      });
    }
  }

  onUserAction(_action: string) {
    
  }

  selectActionTab(tab: 'manage' | 'invite'): void {
    this.activeActionTab.set(tab);
  }

  onSendEmail(confirmResend: boolean = false): void {
    if (!this.userData?.id || !this.userData?.email) {
      console.error('Cannot send invite: no user ID or email available');
      return;
    }

    this.isSendingInvite.set(true);

    let needsResend = false;

    // Chain token acquisition and invite sending using RxJS
    from(this.getAccessTokenForInvite())
      .pipe(
        switchMap(accessToken => this.executeInviteWorkflow(accessToken, confirmResend)),
        tap((response) => { needsResend = this.handleInviteResponse(response); }),
        catchError(error => this.handleInviteSendError(error)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          if (needsResend) {
            // Resend confirmed: keep isSendingInvite=true and start new request
            this.onSendEmail(true);
          } else {
            this.isSendingInvite.set(false);
          }
        },
        error: (error) => {
          this.isSendingInvite.set(false);
          console.error('Invite workflow error:', error);
        }
      });
  }

  private executeInviteWorkflow(accessToken: string, confirmResend: boolean = false) {
    const inviteRequest: SendUserInviteRequest = {
      UserId: this.userData?.id ?? '',
      EmailAddress: this.userData?.email ?? '',
      AccessToken: accessToken,
      RedirectUrl: globalThis.location.origin + '/dashboard',
      ApiBaseUrl: globalThis.location.origin,  // Use frontend origin as API base URL for tracking links
      ConfirmResend: confirmResend
    };
    return this.usersService.sendUserInvite(inviteRequest, accessToken);
  }

  private handleInviteResponse(response: SendUserInviteResponse | null): boolean {
    if (!response) {
      return false;
    }

    if (response.requiresConfirmation) {
      let confirmMessage: string;
      
      if (response.previousSentAt) {
        // Recent invitation was sent
        const sentDate = this.dateFormatterService.formatDateTime(new Date(response.previousSentAt));
        confirmMessage = `An invitation was already sent to this user on ${sentDate}.\n\nDo you want to send another invitation?`;
      } else if (response.previousRedeemedAt) {
        // Previous invitation was redeemed
        const redeemedDate = this.dateFormatterService.formatDateTime(new Date(response.previousRedeemedAt));
        confirmMessage = `A previous invitation was already redeemed on ${redeemedDate}.\n\nDo you want to send a new invitation anyway?`;
      } else {
        confirmMessage = response.message;
      }
      
      return confirm(confirmMessage);
    } else if (response.success) {
      // Update user status to PendingFirstLogin (2) after successful invitation
      if (this.userData) {
        this.userData.status = 2; // PendingFirstLogin
        
        // Emit status change event to parent component so UI updates immediately
        if (this.userData.id) {
          this.userStatusChanged.emit({ userId: this.userData.id, status: 2 });
        }
      }
      
      alert('Invitation sent successfully!');
    } else {
      alert('Failed to send invitation: ' + response.message);
    }

    return false;
  }

  private handleInviteSendError(error: unknown) {
    console.error('Failed to send user invite:', error);
    alert('Failed to send invitation: ' + (error instanceof Error ? error.message : 'Unexpected error'));
    return of(null);
  }

  private async getAccessTokenForInvite(): Promise<string> {
    // Replicate the logic from UserSessionService#getAccessToken
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const msalService = (this.userSessionService as any)._msalService;
      const accounts = msalService.instance.getAllAccounts();
      if (accounts.length === 0) {
        throw new Error('No authenticated accounts found. Please log in again.');
      }
      const account = this.getOrSetActiveAccountForInvite(msalService, accounts);
      return await this.acquireTokenWithAccountForInvite(msalService, environment.apiConfig.scopes, account);
    } catch (error) {
      console.error('Error getting access token for invite:', error);
      throw error;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getOrSetActiveAccountForInvite(msalService: any, accounts: unknown[]): unknown {
    let account = msalService.instance.getActiveAccount();
    if (!account && accounts.length > 0) {
      account = accounts[0];
      msalService.instance.setActiveAccount(account);
    }
    return account;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async acquireTokenWithAccountForInvite(msalService: any, scopes: string[], account: unknown): Promise<string> {
    const tokenRequest = {
      scopes: scopes,
      account: account
    };
    try {
      const silentResult = msalService.acquireTokenSilent(tokenRequest);
      // Handle both Observable (real MSAL) and Promise (mock)
      const response = typeof silentResult?.subscribe === 'function'
        ? await firstValueFrom(silentResult)
        : await silentResult;
      return response.accessToken;
    } catch (silentError) {
      // Silent token acquisition failed, log and try interactive
      console.warn('Silent token acquisition failed; attempting interactive acquisition.', silentError);
      try {
        const popupResult = msalService.acquireTokenPopup(tokenRequest);
        // Handle both Observable (real MSAL) and Promise (mock)
        const response = typeof popupResult?.subscribe === 'function'
          ? await firstValueFrom(popupResult)
          : await popupResult;
        return response.accessToken;
      } catch (popupError) {
        // Interactive also failed; rethrow to caller
        console.error('Interactive token acquisition failed.', popupError);
        throw popupError;
      }
    }
  }

  onDeleteUser(): void {
    if (!this.userData?.id) {
      console.error('Cannot delete user: no user ID available');
      return;
    }

    // Show confirmation dialog
    const userName = this.userData.name || this.userData.email || 'this user';
    const confirmed = confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`);
    
    if (!confirmed) {
      return;
    }

    this.isDeleting.set(true);

    // Capture stable ID for async usage
    const userId = this.userData.id;

    this.usersService.deleteUser(userId).subscribe({
      next: () => {
        this.isDeleting.set(false);
        
        // Emit the deleted user ID
        this.deleteUser.emit(userId);
        
        // Close the modal
        this.closeModal.emit();
      },
      error: (error) => {
        console.error('Failed to delete user:', error);
        this.isDeleting.set(false);
        
        // Check if this is an Entra deletion failure (portal user already deleted)
        const errorMessage = error.message || '';
        const isEntraDeleteFailure = errorMessage.toLowerCase().includes('entra') && 
                                     errorMessage.toLowerCase().includes('successfully deleted from the portal');
        
        if (isEntraDeleteFailure) {
          // Portal user was deleted successfully, only Entra deletion failed
          // Still emit the delete event and close modal since portal user is gone
          this.deleteUser.emit(userId);
          this.closeModal.emit();
          
          // Show warning about Entra deletion failure
          alert('Warning: ' + errorMessage);
        } else {
          // Actual deletion failure - keep modal open
          alert('Failed to delete user: ' + errorMessage);
        }
      }
    });
  }

  onActivateUser(): void {
    if (!this.userData?.email) {
      console.error('Cannot activate user: no email available');
      return;
    }

    const userName = this.userData.name || this.userData.email;
    const confirmed = confirm(`Activate user ${userName}?\n\nThis will allow the user to access the portal.`);

    if (!confirmed) {
      return;
    }

    this.isUpdating.set(true);
    const userEmail = this.userData.email;

    // Status: 3 = Active, 5 = Inactive (new enum values)
    this.usersService.updateUserStatus(userEmail, 3).subscribe({
      next: () => {
        this.isUpdating.set(false);
        // Update the local user data status
        if (this.userData) {
          this.userData.status = 3;
          // Emit status change event to parent component
          if (this.userData.id) {
            this.userStatusChanged.emit({ userId: this.userData.id, status: 3 });
          }
        }
        alert('User has been activated successfully!');
      },
      error: (error) => {
        console.error('Failed to activate user:', error);
        this.isUpdating.set(false);
        alert('Failed to activate user: ' + error.message);
      }
    });
  }

  onDeactivateUser(): void {
    if (!this.userData?.email) {
      console.error('Cannot deactivate user: no email available');
      return;
    }

    const userName = this.userData.name || this.userData.email;
    const confirmed = confirm(`Deactivate user ${userName}?\n\nThis will prevent the user from accessing the portal.`);

    if (!confirmed) {
      return;
    }

    this.isUpdating.set(true);
    const userEmail = this.userData.email;

    // Status: 3 = Active, 5 = Inactive (new enum values)
    this.usersService.updateUserStatus(userEmail, 5).subscribe({
      next: () => {
        this.isUpdating.set(false);
        // Update the local user data status
        if (this.userData) {
          this.userData.status = 5;
          // Emit status change event to parent component
          if (this.userData.id) {
            this.userStatusChanged.emit({ userId: this.userData.id, status: 5 });
          }
        }
        alert('User has been deactivated successfully!');
      },
      error: (error) => {
        console.error('Failed to deactivate user:', error);
        this.isUpdating.set(false);
        alert('Failed to deactivate user: ' + error.message);
      }
    });
  }

  onResetTermsConditions(): void {
    if (!this.userData?.id) {
      console.error('Cannot reset terms: no user ID available');
      return;
    }

    const userName = this.userData.name || this.userData.email || 'this user';
    const confirmed = confirm(`Are you sure you want to reset the Terms & Conditions for ${userName}? They will be required to accept them again on their next login.`);

    if (!confirmed) {
      return;
    }

    this.isResettingTerms.set(true);
    const userId = this.userData.id;

    this.usersService.resetTermsAcceptance(userId).subscribe({
      next: (response) => {
        this.isResettingTerms.set(false);
        if (this.userData) {
          this.userData.termsAccepted = response.hasAcceptedTerms;
          // Emit the updated user data so parent component can update its cached data
          this.termsReset.emit(this.userData);
        }
        alert('Terms & Conditions have been reset for this user.');
      },
      error: (error) => {
        console.error('Failed to reset terms acceptance:', error);
        this.isResettingTerms.set(false);
        alert('Failed to reset terms: ' + error.message);
      }
    });
  }

  /**
   * Gets the display label for a user status
   * @param status The status code (1-5)
   * @returns Human-readable status label
   */
  getStatusLabel(status?: number): string {
    switch (status) {
      case 1: return 'Draft';
      case 2: return 'Pending First Login';
      case 3: return 'Active';
      case 4: return 'Sync Error';
      case 5: return 'Inactive';
      default: return 'Unknown';
    }
  }

  /**
   * Gets the CSS class for a status badge
   * @param status The status code (1-5)
   * @returns CSS class for the status badge
   */
  getStatusClass(status?: number): string {
    switch (status) {
      case 1: return 'status-draft';
      case 2: return 'status-pending';
      case 3: return 'status-active';
      case 4: return 'status-error';
      case 5: return 'status-inactive';
      default: return '';
    }
  }

}

